import { autoUpdate, offset, shift, useFloating } from "@floating-ui/react";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@headlessui/react";
import type { ReactNode } from "react";
import { ControlContainer } from "../elements/common";

interface ToggleButtonProps {
    icon: IconProp;
    label: string;
    enabled: boolean;
    selected: boolean;
    onClick?: () => void;
    className?: string;
}

export const ToggleButton = (props: ToggleButtonProps) => {
    return (
        <button
            type="button"
            onClick={props.enabled ? props.onClick : () => null}
            className={`
                w-12 p-1
                text-xs flex items-center justify-center flex-col
                whitespace-nowrap
                ${
                    !props.enabled
                        ? "bg-ae-gray-100 text-gray-400 cursor-default"
                        : props.selected
                          ? "text-ae-blue-550 bg-ae-blue-40"
                          : "bg-white hover:text-ae-blue-550"
                }
                ${props.className}
            `}
        >
            <FontAwesomeIcon icon={props.icon} className="w-4 h-4" />
            {props.label}
        </button>
    );
};

interface ToolBarDropdownProps {
    children: ReactNode;
    icon: IconProp;
    label: string;
    enabled: boolean;
    className?: string;
}

export const ToolBarDropdown = (props: ToolBarDropdownProps) => {
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [
            offset({
                mainAxis: 6,
                crossAxis: -4,
            }),
            shift(),
        ],
        whileElementsMounted: autoUpdate,
    });

    return (
        <Popover className="relative" ref={refs.setReference}>
            {({ open }) => (
                <>
                    <Popover.Button>
                        <ToggleButton
                            enabled={props.enabled}
                            icon={props.icon}
                            label={props.label}
                            selected={open}
                            className={props.className}
                        />
                    </Popover.Button>

                    <Popover.Panel
                        ref={refs.setFloating}
                        style={floatingStyles}
                        className="z-50"
                    >
                        <ControlContainer className="p-0.5 drop-shadow-xl">
                            <div className="rounded bg-white p-1">
                                {props.children}
                            </div>
                        </ControlContainer>
                    </Popover.Panel>
                </>
            )}
        </Popover>
    );
};

interface NumberInputProps {
    value: number | null;
    onChange: (value: number | null) => void;
    label: string;
}

export const NumberInput = (props: NumberInputProps) => {
    const handleSetValue = (e) => {
        if (e.target.value === "") {
            props.onChange(null);
        } else {
            const val = parseInt(e.target.value, 10);
            props.onChange(Number.isNaN(val) ? null : val);
        }
    };

    return (
        <label className="flex border rounded items-center gap-2 text-xs p-1">
            <span className="text-ae-slate-600">{props.label}</span>
            <input
                className="border-none p-0 text-xs w-24 rounded px-1 ring-0"
                value={props.value === null ? "" : props.value}
                onChange={handleSetValue}
                placeholder="kg/h"
            />
        </label>
    );
};

interface InsightsContainerProps {
    children: ReactNode;
    className?: string;
}

export const InsightsContainer = (props: InsightsContainerProps) => {
    return (
        <div className={`rounded ${props.className}`}>
            <div className="bg-white rounded p-1 border border-ae-gray-300 h-full w-full">
                {props.children}
            </div>
        </div>
    );
};
